import { getKBName } from '@/modules/gpt-chats/components/knowledge-base/utils';
import { useTranslation } from 'react-i18next';
import { useKBFilterQueryParams } from './kb-documents-table-filter-toolbar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { KBType, KB, EmbeddedStatus } from '@/modules/gpt-chats/models/kb';
import { EmbedStatus } from '@/modules/gpt-chats/components/knowledge-base/components/kb-embed-status';
import { AddKbFileModal } from './add-kb-modals/add-kb-file';
import { AddKbWriteupModal } from './add-kb-modals/add-kb-writeup';
import { AddKbQaModal } from './add-kb-modals/add-kb-qa';
import { AddKbLinkModal } from './add-kb-modals/add-kb-link';
import { parseBackendUtc } from '@/modules/gpt-chats/utils/date-utils';
import { FolderRowActionDropdown } from './actions-dropdown';
import { Pagination } from '@/components/ui-kit/pagination';
import { formatFullDate } from '@/modules/gpt-chats/utils/date-utils';
import { useMemo } from 'react';

interface KBDocumentsTableProps {
  knowledgeBases: KB[];
  isLoading: boolean;
  type: KBType;
  folderId: string;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (kb: KB) => void;
}

export const KBDocumentsTable = ({
  knowledgeBases,
  isLoading,
  type,
  totalCount,
  folderId,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowClick,
}: KBDocumentsTableProps) => {
  const { t } = useTranslation();
  const { queryParams } = useKBFilterQueryParams();

  const filteredKnowledgeBases = useMemo(() => {
    let res = knowledgeBases;

    if (type === KBType.FILE && queryParams.fileTypes && queryParams.fileTypes.length > 0) {
      res = res.filter((kb) => {
        const name = kb.file?.name;
        if (!name) return false;
        return queryParams.fileTypes.some((t: string) =>
          name.toLowerCase().endsWith(t.toLowerCase())
        );
      });
    }

    if (queryParams.createStartDate || queryParams.createEndDate) {
      const start = queryParams.createStartDate ? new Date(queryParams.createStartDate) : null;
      const end = queryParams.createEndDate ? new Date(queryParams.createEndDate) : null;

      if (end) end.setHours(23, 59, 59, 999);

      res = res.filter((kb) => {
        const date = new Date(kb.created_date);
        if (start && date < start) return false;
        if (end && date > end) return false;
        return true;
      });
    }

    return res;
  }, [knowledgeBases, queryParams, type]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-row justify-between">
        <div className="mb-4">{/* <KBDocumentsTableToolbar activeTab={type} /> */}</div>
        {getModal(type, folderId)}
      </div>
      <Table className="text-sm">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {/* TODO: add file type */}
            <TableHead className="text-high-emphasis">{t('NAME')}</TableHead>
            {type === KBType.FILE && (
              <TableHead className="text-high-emphasis">{t('FILE_TYPE')}</TableHead>
            )}
            <TableHead className="text-high-emphasis">{t('STATUS')}</TableHead>
            <TableHead className="text-high-emphasis">{t('CHUNKS')}</TableHead>
            <TableHead className="text-high-emphasis">{t('LAST_UPDATED')}</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredKnowledgeBases.length > 0 ? (
            filteredKnowledgeBases.map((kb) => (
              <TableRow
                key={kb.id}
                className={`hover:bg-muted/50 ${onRowClick && kb.embad_status === EmbeddedStatus.COMPLETED ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (kb.embad_status === EmbeddedStatus.COMPLETED) {
                    onRowClick?.(kb);
                  }
                }}
              >
                <TableCell className="text-muted-foreground">{getKBName(kb, t)}</TableCell>
                {type === KBType.FILE && (
                  <TableCell className="text-muted-foreground">
                    {kb.file?.name?.split('.').pop() || '-'}
                  </TableCell>
                )}
                <TableCell className="text-muted-foreground">
                  <EmbedStatus status={kb.embad_status} kbId={kb.id} type={type} />
                </TableCell>
                <TableCell className="text-muted-foreground">{kb.total_chunks}</TableCell>
                <TableCell className="text-muted-foreground">
                  {formatFullDate(new Date(parseBackendUtc(kb.last_updated_date)))}
                </TableCell>
                <TableCell>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <FolderRowActionDropdown documentId={kb.id} kbType={type} folderId={folderId} />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={type === KBType.FILE ? 6 : 5}
                className="h-24 text-center text-muted-foreground"
              >
                {t('NO_DATA')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {totalCount > pageSize && (
        <div className="mt-4 flex flex-col items-center justify-end gap-2 md:flex-row">
          <Pagination
            page={page}
            onChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            totalCount={totalCount}
            pageSize={pageSize}
            pageSizeOptions={[10, 50, 100]}
          />
        </div>
      )}
    </>
  );
};

const getModal = (type: KBType, folderId: string) => {
  switch (type) {
    case KBType.FILE:
      return <AddKbFileModal folderId={folderId} />;

    case KBType.TEXT:
      return <AddKbWriteupModal folderId={folderId} />;

    case KBType.QnA:
      return <AddKbQaModal folderId={folderId} />;
    case KBType.LINK:
      return <AddKbLinkModal folderId={folderId} />;

    default:
      return null;
  }
};
